import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  ChevronLeft, Plus, Trash2, Package, RotateCcw,
  Building2, Loader2, FileText, AlertTriangle, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VendorReturnCreate = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [returnReasons, setReturnReasons] = useState([]);

  // Vendor-derived data
  const [vendorPOs, setVendorPOs] = useState([]);
  const [vendorCompanyCodes, setVendorCompanyCodes] = useState([]);
  const [vendorPurchOrgs, setVendorPurchOrgs] = useState([]);
  const [loadingVendorData, setLoadingVendorData] = useState(false);

  // Selected PO data
  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedPOItem, setSelectedPOItem] = useState(null);

  const [formData, setFormData] = useState({
    vendor_id: '',
    company_code: '',
    purchasing_organization: '',
    return_date: new Date().toISOString().split('T')[0],
    header_text: '',
    create_credit_memo: true,
    created_by: 'ADMIN'
  });

  const [items, setItems] = useState([]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({
    po_number: '', po_item: '',
    return_quantity: '', return_reason: '', reason_text: '',
    plant: '', storage_location: '', stock_type: '', batch: '',
    gr_document: '', gr_item: '',
    material_id: '', material_description: '', unit_of_measure: 'EA',
    unit_price: 0, available_quantity: 0
  });

  const fetchInitialData = useCallback(async () => {
    const [vendorsRes, reasonsRes] = await Promise.all([
      axios.get(`${API}/api/vendor-master/vendors`).catch(() => ({ data: [] })),
      axios.get(`${API}/api/vendor-returns/return-reasons`).catch(() => ({ data: [] }))
    ]);
    setVendors(vendorsRes.data || []);
    setReturnReasons(reasonsRes.data || []);
  }, []);

  useEffect(() => { fetchInitialData(); }, [fetchInitialData]);

  const handleVendorChange = async (vendorId) => {
    setFormData(prev => ({ ...prev, vendor_id: vendorId, company_code: '', purchasing_organization: '' }));
    setVendorPOs([]);
    setItems([]);
    setSelectedPO(null);
    if (!vendorId) return;

    setLoadingVendorData(true);
    try {
      // Fetch POs for vendor + vendor assignments in parallel
      const [posRes, vccRes, vpRes] = await Promise.all([
        axios.get(`${API}/api/purchase-orders`, { params: { vendor_id: vendorId } }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/vendor-master/vendor-company-codes`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/vendor-master/vendor-purchasing-orgs`).catch(() => ({ data: [] }))
      ]);

      // Filter POs for this vendor
      const allPOs = Array.isArray(posRes.data) ? posRes.data : [];
      const filteredPOs = allPOs.filter(po => po.vendor_id === vendorId);
      setVendorPOs(filteredPOs);

      // Filter vendor company codes
      const vcc = (vccRes.data || []).filter(v => v.vendor_number === vendorId);
      setVendorCompanyCodes(vcc);

      // Filter vendor purchasing orgs
      const vpo = (vpRes.data || []).filter(v => v.vendor_number === vendorId);
      setVendorPurchOrgs(vpo);

      // Auto-derive company code and purchasing org
      // Priority: vendor master assignments, then from first PO
      let derivedCC = '';
      let derivedPOrg = '';

      if (vcc.length > 0) {
        derivedCC = vcc[0].company_code_id;
      } else if (filteredPOs.length > 0) {
        derivedCC = filteredPOs[0].company_code || '';
      }

      if (vpo.length > 0) {
        derivedPOrg = vpo[0].purchasing_org_id;
      } else if (filteredPOs.length > 0) {
        derivedPOrg = filteredPOs[0].purchasing_org || '';
      }

      setFormData(prev => ({
        ...prev,
        company_code: derivedCC,
        purchasing_organization: derivedPOrg
      }));

      if (filteredPOs.length === 0) {
        toast.info('No purchase orders found for this vendor');
      }
    } catch (error) {
      console.error('Error fetching vendor data:', error);
    } finally {
      setLoadingVendorData(false);
    }
  };

  const handlePOChange = (poNumber) => {
    const po = vendorPOs.find(p => p.po_number === poNumber);
    setSelectedPO(po || null);
    setSelectedPOItem(null);
    setNewItem(prev => ({
      ...prev,
      po_number: poNumber,
      po_item: '',
      return_quantity: '',
      plant: '', storage_location: '',
      material_id: '', material_description: '', unit_of_measure: 'EA',
      unit_price: 0, available_quantity: 0
    }));

    // Also update header from PO if not already set
    if (po) {
      setFormData(prev => ({
        ...prev,
        company_code: prev.company_code || po.company_code || '',
        purchasing_organization: prev.purchasing_organization || po.purchasing_org || ''
      }));
    }
  };

  const handlePOItemChange = (itemNumber) => {
    if (!selectedPO) return;
    const item = (selectedPO.items || []).find(i => String(i.item_number) === String(itemNumber));
    setSelectedPOItem(item || null);

    if (item) {
      const qty = item.po_quantity || item.quantity || 0;
      setNewItem(prev => ({
        ...prev,
        po_item: String(item.item_number),
        plant: item.plant || '',
        storage_location: item.storage_location || '',
        material_id: item.material_id || item.material_number || '',
        material_description: item.short_text || item.description || '',
        unit_of_measure: item.unit_of_measure || 'EA',
        unit_price: item.net_price || 0,
        available_quantity: qty,
        return_quantity: ''
      }));
    }
  };

  const openAddItemDialog = () => {
    setSelectedPO(null);
    setSelectedPOItem(null);
    setNewItem({
      po_number: '', po_item: '',
      return_quantity: '', return_reason: '', reason_text: '',
      plant: '', storage_location: '', stock_type: '', batch: '',
      gr_document: '', gr_item: '',
      material_id: '', material_description: '', unit_of_measure: 'EA',
      unit_price: 0, available_quantity: 0
    });
    setShowAddItem(true);
  };

  const confirmAddItem = () => {
    if (!newItem.po_number) { toast.error('Please select a Purchase Order'); return; }
    if (!newItem.po_item) { toast.error('Please select a PO Item'); return; }
    if (!newItem.return_quantity || parseFloat(newItem.return_quantity) <= 0) { toast.error('Enter a valid return quantity'); return; }
    if (!newItem.return_reason) { toast.error('Please select a return reason'); return; }
    if (!newItem.plant) { toast.error('Plant is required'); return; }
    if (!newItem.storage_location) { toast.error('Storage location is required'); return; }

    const itemNumber = (items.length + 1) * 10;
    const returnQty = parseFloat(newItem.return_quantity);
    setItems(prev => [...prev, {
      ...newItem,
      item_number: itemNumber,
      return_quantity: returnQty,
      po_item: parseInt(newItem.po_item),
      net_value: returnQty * (newItem.unit_price || 0)
    }]);
    setShowAddItem(false);
  };

  const removeItem = (index) => setItems(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async () => {
    if (!formData.vendor_id) { toast.error('Please select a vendor'); return; }
    if (!formData.company_code) { toast.error('Company code is required'); return; }
    if (!formData.purchasing_organization) { toast.error('Purchasing organization is required'); return; }
    if (items.length === 0) { toast.error('Add at least one return item'); return; }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        items: items.map(item => ({
          po_number: item.po_number,
          po_item: item.po_item,
          gr_document: item.gr_document || null,
          gr_item: item.gr_item ? parseInt(item.gr_item) : null,
          return_quantity: item.return_quantity,
          return_reason: item.return_reason,
          reason_text: item.reason_text || null,
          plant: item.plant,
          storage_location: item.storage_location,
          stock_type: item.stock_type || '',
          batch: item.batch || null
        }))
      };
      const response = await axios.post(`${API}/api/vendor-returns/returns`, payload);
      toast.success(`Return ${response.data.return_number} created`);
      navigate(`/vendor-returns/${response.data.return_number}`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create return');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
  const totalValue = items.reduce((sum, i) => sum + (i.net_value || 0), 0);
  const totalQty = items.reduce((sum, i) => sum + (i.return_quantity || 0), 0);

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="vendor-return-create">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/vendor-returns')} data-testid="back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-rose-600 rounded-lg"><RotateCcw className="h-5 w-5 text-white" /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Create Vendor Return</h1>
              <p className="text-sm text-slate-500">Return goods to vendor and process credit</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="header" className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="header" data-testid="tab-header">Header Data</TabsTrigger>
            <TabsTrigger value="items" data-testid="tab-items">Items ({items.length})</TabsTrigger>
          </TabsList>

          {/* Header Tab */}
          <TabsContent value="header">
            <Card className="border-0 shadow-sm">
              <CardContent className="pt-6 space-y-6">
                {/* Vendor Selection */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Vendor <span className="text-red-500">*</span></Label>
                    <Select value={formData.vendor_id || '__none__'} onValueChange={(v) => handleVendorChange(v === '__none__' ? '' : v)}>
                      <SelectTrigger className="bg-slate-50" data-testid="form-vendor">
                        <SelectValue placeholder="Select vendor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select vendor</SelectItem>
                        {vendors.map(v => (
                          <SelectItem key={v.vendor_number} value={v.vendor_number}>
                            {v.vendor_number} - {v.name || 'Unknown'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {loadingVendorData && <p className="text-xs text-orange-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading vendor data...</p>}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Return Date</Label>
                    <Input type="date" value={formData.return_date} onChange={(e) => setFormData(prev => ({...prev, return_date: e.target.value}))} className="bg-slate-50" data-testid="form-return-date" />
                  </div>
                </div>

                {/* Auto-derived fields */}
                {formData.vendor_id && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-4">
                    <div className="flex items-center gap-2">
                      <Info className="h-4 w-4 text-orange-600" />
                      <span className="text-sm font-medium text-orange-700">Derived from Vendor</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Company Code <span className="text-red-500">*</span></Label>
                        <Input
                          value={formData.company_code}
                          onChange={(e) => setFormData(prev => ({...prev, company_code: e.target.value}))}
                          className="bg-white"
                          placeholder={loadingVendorData ? 'Loading...' : 'Auto-derived from vendor'}
                          data-testid="form-company-code"
                        />
                        {vendorCompanyCodes.length > 1 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {vendorCompanyCodes.map(cc => (
                              <button key={cc.company_code_id} onClick={() => setFormData(prev => ({...prev, company_code: cc.company_code_id}))}
                                className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${formData.company_code === cc.company_code_id ? 'bg-orange-100 border-orange-400 text-orange-700' : 'bg-white border-slate-200 text-slate-600 hover:border-orange-300'}`}
                                data-testid={`cc-option-${cc.company_code_id}`}>
                                {cc.company_code_id}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Purchasing Organization <span className="text-red-500">*</span></Label>
                        <Input
                          value={formData.purchasing_organization}
                          onChange={(e) => setFormData(prev => ({...prev, purchasing_organization: e.target.value}))}
                          className="bg-white"
                          placeholder={loadingVendorData ? 'Loading...' : 'Auto-derived from vendor'}
                          data-testid="form-purch-org"
                        />
                        {vendorPurchOrgs.length > 1 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {vendorPurchOrgs.map(po => (
                              <button key={po.purchasing_org_id} onClick={() => setFormData(prev => ({...prev, purchasing_organization: po.purchasing_org_id}))}
                                className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${formData.purchasing_organization === po.purchasing_org_id ? 'bg-orange-100 border-orange-400 text-orange-700' : 'bg-white border-slate-200 text-slate-600 hover:border-orange-300'}`}
                                data-testid={`porg-option-${po.purchasing_org_id}`}>
                                {po.purchasing_org_id}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* PO Summary for selected vendor */}
                {vendorPOs.length > 0 && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">
                      <FileText className="inline h-4 w-4 mr-1 text-slate-400" />
                      <strong>{vendorPOs.length}</strong> Purchase Order(s) found for this vendor
                    </p>
                  </div>
                )}

                <div className="col-span-2 space-y-2">
                  <Label className="text-sm font-medium">Header Text</Label>
                  <Textarea value={formData.header_text} onChange={(e) => setFormData(prev => ({...prev, header_text: e.target.value}))} placeholder="Notes about this return..." className="bg-slate-50" data-testid="form-header-text" />
                </div>

                <div className="flex items-center gap-3">
                  <input type="checkbox" id="credit-memo" checked={formData.create_credit_memo} onChange={(e) => setFormData(prev => ({...prev, create_credit_memo: e.target.checked}))} className="h-4 w-4 rounded border-slate-300" data-testid="form-credit-memo" />
                  <Label htmlFor="credit-memo" className="text-sm cursor-pointer">Auto-create vendor credit memo</Label>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Items Tab */}
          <TabsContent value="items">
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-semibold">Return Items</CardTitle>
                <Button size="sm" variant="outline" onClick={openAddItemDialog} disabled={!formData.vendor_id} data-testid="add-item-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
                </Button>
              </CardHeader>
              <CardContent>
                {!formData.vendor_id ? (
                  <div className="text-center py-12 text-slate-500">
                    <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-amber-400" />
                    <p>Select a vendor first to add return items.</p>
                  </div>
                ) : items.length > 0 ? (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="text-xs">#</TableHead>
                          <TableHead className="text-xs">PO / Item</TableHead>
                          <TableHead className="text-xs">Material</TableHead>
                          <TableHead className="text-xs text-right">Return Qty</TableHead>
                          <TableHead className="text-xs">Reason</TableHead>
                          <TableHead className="text-xs">Plant / SLoc</TableHead>
                          <TableHead className="text-xs text-right">Value</TableHead>
                          <TableHead className="text-xs w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {items.map((item, index) => (
                          <TableRow key={`${item.po_number}-${item.po_item}-${index}`} data-testid={`return-item-row-${index}`}>
                            <TableCell className="font-mono text-xs">{item.item_number}</TableCell>
                            <TableCell className="text-sm">{item.po_number} / {item.po_item}</TableCell>
                            <TableCell>
                              <p className="text-sm font-medium">{item.material_id || '-'}</p>
                              <p className="text-xs text-slate-500 truncate max-w-[180px]">{item.material_description || ''}</p>
                            </TableCell>
                            <TableCell className="text-right text-sm font-medium">{item.return_quantity} {item.unit_of_measure}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {returnReasons.find(r => r.value === item.return_reason)?.label || item.return_reason}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">{item.plant} / {item.storage_location}</TableCell>
                            <TableCell className="text-right text-sm font-medium">{formatCurrency(item.net_value)}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-red-50" onClick={() => removeItem(index)} data-testid={`remove-item-${index}`}>
                                <Trash2 className="h-3.5 w-3.5 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="flex justify-between items-center mt-4 p-3 bg-slate-50 rounded-lg">
                      <span className="text-sm text-slate-600">{items.length} items, Total Qty: {totalQty}</span>
                      <span className="text-lg font-bold text-rose-700" data-testid="total-value">Total: {formatCurrency(totalValue)}</span>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <Package className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                    <p>No items added. Click "Add Item" to select from PO items.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Item Dialog */}
        {showAddItem && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" data-testid="add-item-dialog">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 m-4">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Package className="h-5 w-5 text-rose-600" />
                Add Return Item
              </h3>

              <div className="space-y-4">
                {/* PO Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Purchase Order <span className="text-red-500">*</span></Label>
                    <Select value={newItem.po_number || '__none__'} onValueChange={(v) => handlePOChange(v === '__none__' ? '' : v)}>
                      <SelectTrigger className="bg-slate-50" data-testid="item-po-number">
                        <SelectValue placeholder="Select PO" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select Purchase Order</SelectItem>
                        {vendorPOs.map(po => (
                          <SelectItem key={po.po_number} value={po.po_number}>
                            {po.po_number} ({(po.items || []).length} items)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">PO Item <span className="text-red-500">*</span></Label>
                    <Select
                      value={newItem.po_item || '__none__'}
                      onValueChange={(v) => handlePOItemChange(v === '__none__' ? '' : v)}
                      disabled={!selectedPO}
                    >
                      <SelectTrigger className="bg-slate-50" data-testid="item-po-item">
                        <SelectValue placeholder={selectedPO ? "Select item" : "Select PO first"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select PO item</SelectItem>
                        {selectedPO && (selectedPO.items || []).map(item => (
                          <SelectItem key={item.item_number} value={String(item.item_number)}>
                            Item {item.item_number} - {item.material_id || item.short_text || 'No material'} (Qty: {item.po_quantity || item.quantity || 0})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Auto-populated material info */}
                {newItem.material_id && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Package className="h-4 w-4 text-orange-600" />
                      <span className="text-sm font-semibold text-orange-700">Material Details (from PO)</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div><p className="text-xs text-orange-500">Material</p><p className="font-medium text-orange-900" data-testid="auto-material">{newItem.material_id}</p></div>
                      <div className="col-span-2"><p className="text-xs text-orange-500">Description</p><p className="font-medium text-orange-900" data-testid="auto-description">{newItem.material_description || '-'}</p></div>
                      <div><p className="text-xs text-orange-500">PO Quantity</p><p className="font-medium text-orange-900" data-testid="auto-po-qty">{newItem.available_quantity} {newItem.unit_of_measure}</p></div>
                      <div><p className="text-xs text-orange-500">Unit Price</p><p className="font-medium text-orange-900" data-testid="auto-price">{formatCurrency(newItem.unit_price)}</p></div>
                    </div>
                  </div>
                )}

                {/* Auto-populated plant/sloc */}
                {(newItem.plant || newItem.storage_location) && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm font-semibold text-emerald-700">Location (from PO Item)</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><p className="text-xs text-emerald-500">Plant</p><p className="font-medium text-emerald-900" data-testid="auto-plant">{newItem.plant}</p></div>
                      <div><p className="text-xs text-emerald-500">Storage Location</p><p className="font-medium text-emerald-900" data-testid="auto-sloc">{newItem.storage_location}</p></div>
                    </div>
                  </div>
                )}

                {/* Return-specific fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Return Quantity <span className="text-red-500">*</span></Label>
                    <Input
                      type="number" min="0" step="0.01"
                      max={newItem.available_quantity || undefined}
                      placeholder={newItem.available_quantity ? `Max: ${newItem.available_quantity}` : '0'}
                      value={newItem.return_quantity}
                      onChange={(e) => setNewItem(prev => ({...prev, return_quantity: e.target.value}))}
                      className="bg-slate-50"
                      data-testid="item-return-qty"
                    />
                    {newItem.return_quantity && newItem.unit_price > 0 && (
                      <p className="text-xs text-slate-500">
                        Return value: <strong>{formatCurrency(parseFloat(newItem.return_quantity) * newItem.unit_price)}</strong>
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Return Reason <span className="text-red-500">*</span></Label>
                    <Select value={newItem.return_reason || '__none__'} onValueChange={(v) => setNewItem(prev => ({...prev, return_reason: v === '__none__' ? '' : v}))}>
                      <SelectTrigger className="bg-slate-50" data-testid="item-reason">
                        <SelectValue placeholder="Select reason" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select reason</SelectItem>
                        {returnReasons.map(r => (
                          <SelectItem key={r.value} value={r.value}>{r.value} - {r.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">Reason Text</Label>
                  <Input value={newItem.reason_text} onChange={(e) => setNewItem(prev => ({...prev, reason_text: e.target.value}))} placeholder="Additional details..." className="bg-slate-50" data-testid="item-reason-text" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm">GR Document</Label>
                    <Input value={newItem.gr_document} onChange={(e) => setNewItem(prev => ({...prev, gr_document: e.target.value}))} placeholder="Optional" className="bg-slate-50" data-testid="item-gr-doc" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">Batch</Label>
                    <Input value={newItem.batch} onChange={(e) => setNewItem(prev => ({...prev, batch: e.target.value}))} placeholder="Optional" className="bg-slate-50" data-testid="item-batch" />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowAddItem(false)} data-testid="item-cancel-btn">Cancel</Button>
                <Button onClick={confirmAddItem} className="bg-rose-600 hover:bg-rose-700" data-testid="item-add-btn">Add Item</Button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between p-4 bg-white rounded-lg shadow-sm border">
          <p className="text-sm text-slate-600">
            {items.length > 0 ? `${items.length} item(s) | Qty: ${totalQty} | ${formatCurrency(totalValue)}` : 'No items added yet'}
          </p>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate('/vendor-returns')} data-testid="cancel-btn">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || items.length === 0} className="bg-rose-600 hover:bg-rose-700" data-testid="submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Create Return
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default VendorReturnCreate;
